import React from 'react';
import { Link } from 'react-router-dom';
import { Mic, Pause, Square, Play, Loader2, AudioLines } from 'lucide-react';
import { Button } from '../ui/button';

export function StorybookPromptVoicePanel({
  voice,
  compact = false,
  fallbackVoiceHref = null,
}) {
  const {
    recordingState,
    recordingTime,
    formatTime,
    continueRecording,
    isUploading,
    isTranscribing,
    transcriptionStatusLabel,
    micPermission,
    recordingCount,
    audioUrl,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    cancelRecording,
    transcribe,
    setContinueRecording,
  } = voice;

  return (
    <div
      className="rounded-xl border border-[hsl(38_14%_86%)]/80 bg-[hsl(42_28%_98%)]/90 px-4 py-3"
      data-testid="storybook-voice-panel"
    >
      <p className="mb-2 flex items-center gap-1.5 text-xs font-medium text-[#8b7355]">
        <Mic className="h-3.5 w-3.5" aria-hidden />
        Record answer
      </p>
      <p className="mb-2 text-[11px] text-muted-foreground/75">
        Write above or record here — transcribe when you want editable text.
      </p>

      {micPermission === 'denied' && fallbackVoiceHref ? (
        <p className="text-xs text-muted-foreground">
          Microphone access is blocked in this browser.{' '}
          <Link to={fallbackVoiceHref} className="font-medium text-[#8b7355] underline underline-offset-2">
            Record in Stories &amp; voice instead
          </Link>
        </p>
      ) : null}

      {recordingState === 'inactive' && micPermission !== 'denied' && (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            className="rounded-full bg-[#8B7355] hover:bg-[#7A6548] text-white gap-1.5"
            disabled={isUploading}
            onClick={startRecording}
            data-testid="storybook-record-btn"
          >
            <Mic className="h-3.5 w-3.5" />
            {continueRecording ? 'Continue recording' : 'Record answer'}
          </Button>
          {recordingCount > 0 && (
            <>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="rounded-full gap-1.5"
                disabled={isTranscribing}
                onClick={transcribe}
                data-testid="storybook-transcribe-btn"
              >
                {isTranscribing ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <AudioLines className="h-3.5 w-3.5" />
                )}
                Transcribe to text
              </Button>
              {audioUrl && (
                <audio controls src={audioUrl} className="h-8 max-w-[200px]" preload="metadata">
                  <track kind="captions" />
                </audio>
              )}
            </>
          )}
        </div>
      )}

      {recordingState === 'recording' && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-red-200/60 bg-red-50/40 px-3 py-2">
          <span className="text-sm font-mono tabular-nums text-red-700">
            Recording {formatTime(recordingTime)}
          </span>
          <div className="flex gap-2">
            <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={pauseRecording}>
              <Pause className="h-3.5 w-3.5 mr-1" /> Pause
            </Button>
            <Button type="button" size="sm" className="rounded-full bg-[#8B7355]" onClick={stopRecording}>
              <Square className="h-3.5 w-3.5 mr-1" /> Stop
            </Button>
            <button type="button" className="text-xs text-muted-foreground" onClick={cancelRecording}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {recordingState === 'paused' && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200/60 bg-amber-50/35 px-3 py-2">
          <span className="text-sm text-amber-800">Paused · {formatTime(recordingTime)}</span>
          <div className="flex gap-2">
            <Button type="button" size="sm" className="rounded-full bg-[#8B7355]" onClick={resumeRecording}>
              <Play className="h-3.5 w-3.5 mr-1" /> Resume
            </Button>
            <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={stopRecording}>
              Stop &amp; save
            </Button>
          </div>
        </div>
      )}

      {(recordingState === 'processing' || isUploading) && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Saving recording…
        </p>
      )}

      {recordingCount > 0 && recordingState === 'inactive' && (
        <p className="mt-2 text-[11px] text-muted-foreground">
          {recordingCount} voice recording{recordingCount === 1 ? '' : 's'} attached
          {transcriptionStatusLabel ? ` · ${transcriptionStatusLabel}` : ''}
        </p>
      )}

      {continueRecording && recordingState === 'inactive' && !compact && (
        <button
          type="button"
          className="mt-2 text-[11px] text-muted-foreground hover:text-foreground"
          onClick={() => setContinueRecording(false)}
        >
          Done adding voice notes
        </button>
      )}
    </div>
  );
}

export default StorybookPromptVoicePanel;
